import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import './components/MicroInteractions.css';
import './components/PulseEffects.css';
import './components/HoverZoom.css';
import HomePage from './components/HomePage';
import BlogPage from './components/blog/BlogPage';
import BlogPost from './components/blog/BlogPost';
import LocalLandingPage from './components/LocalLandingPage';
import AuditLandingPage from './components/AuditLandingPage';
import PricingPage from './components/PricingPage';
import PlansPage from './components/PlansPage';
import PartnersPage from './components/PartnersPage';
import LocalLlmPage from './components/LocalLlmPage';
import WebAppsPage from './components/WebAppsPage';
import LawFirmsPage from './components/LawFirmsPage';
import AboutPage from './components/AboutPage';
import FaqHubPage from './components/FaqHubPage';
import ComparePage, { CompareHub } from './components/ComparePage';
import ResearchPage from './components/ResearchPage';
import VendorLandingPage, { VendorScreeningIndex } from './components/VendorLandingPage';
import WebsitesAdPage from './components/WebsitesAdPage';
import CallBookingPage from './components/CallBookingPage';
import NotFound from './components/NotFound';
import Navigation from './components/Navigation';

import ScrollReveal from './components/ScrollReveal';
import BackToTop from './components/BackToTop';
import SkipToContent from './components/SkipToContent';
import KeyboardNav from './components/KeyboardNav';
import StickyCTABar from './components/StickyCTABar';
import { ToastProvider } from './components/ToastNotification';
import CookieConsent from './components/CookieConsent';
import GoogleAnalytics from './components/GoogleAnalytics';
import Unsubscribe from './components/Unsubscribe';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import { captureAttribution } from './utils/attribution';
// Imported last on purpose: the maturity-pass overrides must bundle after
// every component stylesheet to win equal-specificity cascades.
import './mature.css';
import './studio.css';

// Hidden, heavy, interactive — code-split so it never weighs on the main site.
const Showcase = lazy(() => import('./components/showcase/Showcase'));
// Office TV display (WeWork). Lazy so the QR library stays out of the main bundle.
const TvPage = lazy(() => import('./components/TvPage'));

function App() {
  // The /showcase route is a full-screen immersive demo — suppress all site chrome.
  const { pathname } = useLocation();
  // Hold the ad click (UTMs / gclid) for the session on whichever page they land.
  useEffect(() => { captureAttribution(); }, []);
  const immersive = pathname.startsWith('/showcase') || pathname.startsWith('/tv');
  // Paid-campaign landing pages: strip the site nav and the competing "Free Audit"
  // CTA so the only exits are the page's own form or a single Return-to-home link.
  // Cookie consent stays — it is a legal mechanism, not chrome.
  const campaign = pathname.startsWith('/vendor-screening') || pathname.startsWith('/lp/')
    || pathname.startsWith('/call/');
  const bareChrome = immersive || campaign;

  return (
    <ToastProvider>
      <div className="App">
        <GoogleAnalytics />
        {!immersive && <SkipToContent />}
        {!bareChrome && <Navigation />}
        <ScrollReveal />
        <KeyboardNav />
        {!bareChrome && <StickyCTABar />}
        {!immersive && <CookieConsent />}
        {!bareChrome && <BackToTop />}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/free-missed-lead-audit" element={<AuditLandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* Private rate card: noindex + absent from sitemap + unlinked. Direct URL only. */}
          <Route path="/plans" element={<PlansPage />} />
          {/* Private brochure page for law firms. Same rules as /plans. */}
          <Route path="/localllm" element={<LocalLlmPage />} />
          {/* Everything we build for law firms, shared with prospects by link. noindex. */}
          <Route path="/law-firms" element={<LawFirmsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/web-apps" element={<WebAppsPage />} />
          <Route path="/faq" element={<FaqHubPage />} />
          <Route path="/research" element={<ResearchPage />} />
          {/* Google Ads landing pages — two segments, so they never hit /:slug */}
          <Route path="/lp/websites" element={<WebsitesAdPage />} />
          <Route path="/call/receptionist" element={<CallBookingPage product="receptionist" />} />
          <Route path="/call/website" element={<CallBookingPage product="website" />} />
          <Route path="/call/app" element={<CallBookingPage product="app" />} />
          {/* LinkedIn campaign namespace — two segments, so it never hits /:slug */}
          <Route path="/vendor-screening" element={<VendorScreeningIndex />} />
          <Route path="/vendor-screening/:slug" element={<VendorLandingPage />} />
          <Route path="/compare" element={<CompareHub />} />
          <Route path="/compare/:slug" element={<ComparePage />} />
          <Route
            path="/showcase"
            element={(
              <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0a0a0c' }} />}>
                <Showcase />
              </Suspense>
            )}
          />
          <Route
            path="/tv"
            element={(
              <Suspense fallback={<div style={{ minHeight: '100vh', background: '#09090b' }} />}>
                <TvPage />
              </Suspense>
            )}
          />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/:slug" element={<LocalLandingPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </ToastProvider>
  );
}

export default App;
